import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, FileText, Download, Eye, Edit2, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { StatusBadge, formatCurrency, formatDate, emptyLine } from "@/components/quotations/QuotationShared";
import { QuoteViewDialog, QuoteCreateDialog, QuoteEditDialog } from "@/components/quotations/QuotationDialogs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Quotations() {
  const { user, hasPerm } = useAuth();
  const canEdit = ["admin", "manager"].includes(user?.role);
  const canDelete = user?.role === "admin";
  const canDownload = hasPerm("downloads_allowed");

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [viewOpen, setViewOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedLines, setSelectedLines] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [form, setForm] = useState({ client_id: "", subject: "", machine_model: "", valid_days: 30, customer_notes: "", internal_notes: "" });
  const [formLines, setFormLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);

  const fetchQuotes = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, page_size: 20 });
      if (search) params.append("search", search);
      if (statusFilter !== "all") params.append("status", statusFilter);
      const r = await axios.get(`${API}/quotations?${params}`);
      setQuotes(r.data.quotations || []);
      setTotalPages(r.data.total_pages || 1);
      setTotal(r.data.total || 0);
    } catch (err) { console.error("Quotations fetch failed:", err.message); toast.error("Failed to load quotations"); }
    finally { setLoading(false); }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);
  useEffect(() => {
    const t = setTimeout(() => { if (page !== 1) setPage(1); else fetchQuotes(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  const searchClients = async (q) => {
    setClientSearch(q);
    if (q.length < 2) { setClients([]); return; }
    try { const r = await axios.get(`${API}/clients/search?q=${encodeURIComponent(q)}`); setClients(r.data || []); }
    catch (err) { console.error("Client search failed:", err.message); }
  };

  const viewQuote = async (q) => {
    try {
      const r = await axios.get(`${API}/quotations/${q.id}`);
      setSelected(r.data); setSelectedLines(r.data.lines || []); setViewOpen(true);
    } catch { toast.error("Failed to load quotation"); }
  };

  const openCreate = () => {
    setForm({ client_id: "", subject: "", machine_model: "", valid_days: 30, customer_notes: "", internal_notes: "" });
    setFormLines([emptyLine()]); setClients([]); setClientSearch(""); setCreateOpen(true);
  };

  const openEdit = async (q) => {
    try {
      const r = await axios.get(`${API}/quotations/${q.id}`);
      const d = r.data;
      setSelected(d);
      setForm({ client_id: d.client_id || "", subject: d.subject || "", machine_model: d.machine_model || "", valid_days: d.valid_days || 30, customer_notes: d.customer_notes || "", internal_notes: d.internal_notes || "" });
      const lines = (d.lines || []).map(l => ({ id: l.id, part_number_raw: l.part_number_raw || "", part_name: l.part_name || "", description: l.description || "", qty: l.qty || 1, uom: l.uom || "Nos", brand: l.brand || "", unit_price: l.unit_price || 0, gst_rate: l.gst_rate || 18, hsn_code: l.hsn_code || "", notes: l.notes || "" }));
      setFormLines(lines.length > 0 ? lines : [emptyLine()]);
      setEditOpen(true);
    } catch { toast.error("Failed to load quotation for editing"); }
  };

  const handleCreate = async () => {
    if (!form.client_id) { toast.error("Please select a client"); return; }
    const validLines = formLines.filter(l => l.part_name);
    if (validLines.length === 0) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    try { await axios.post(`${API}/quotations`, { ...form, lines: validLines }); toast.success("Quotation created"); setCreateOpen(false); fetchQuotes(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    finally { setSaving(false); }
  };

  const handleUpdateHeader = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.put(`${API}/quotations/${selected.id}`, { subject: form.subject, machine_model: form.machine_model, valid_days: form.valid_days, customer_notes: form.customer_notes, internal_notes: form.internal_notes });
      for (const line of formLines) {
        if (line.id) await axios.put(`${API}/quotations/${selected.id}/lines/${line.id}`, { part_number_raw: line.part_number_raw, part_name: line.part_name, qty: line.qty, unit_price: line.unit_price, gst_rate: line.gst_rate, uom: line.uom, brand: line.brand, hsn_code: line.hsn_code, notes: line.notes });
      }
      const newLines = formLines.filter(l => !l.id && l.part_name);
      if (newLines.length > 0) await axios.post(`${API}/quotations/${selected.id}/lines`, newLines);
      toast.success("Quotation updated"); setEditOpen(false); fetchQuotes();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    finally { setSaving(false); }
  };

  const approveQuote = async (id) => {
    try { await axios.post(`${API}/quotations/${id}/approve`); toast.success("Quotation approved"); fetchQuotes(); if (viewOpen) viewQuote({ id }); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to approve"); }
  };

  const updateStatus = async (id, status) => {
    try { await axios.post(`${API}/quotations/${id}/status`, { status }); toast.success(`Status updated to ${status}`); fetchQuotes(); if (viewOpen) viewQuote({ id }); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to update status"); }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Delete this quotation?")) return;
    try { await axios.delete(`${API}/quotations/${id}`); toast.success("Quotation deleted"); fetchQuotes(); }
    catch { toast.error("Failed to delete"); }
  };

  const exportQuote = async (id, format) => {
    try {
      const r = await axios.get(`${API}/quotations/${id}/export/${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `quotation.${format === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
  };

  const updateLine = (idx, field, value) => { const u = [...formLines]; u[idx] = { ...u[idx], [field]: value }; setFormLines(u); };
  const addLine = () => setFormLines([...formLines, emptyLine()]);
  const removeLine = (idx) => { if (formLines.length > 1) setFormLines(formLines.filter((_, i) => i !== idx)); };
  const calcLineTotal = (l) => { const t = (l.qty || 0) * (l.unit_price || 0); return t + t * ((l.gst_rate || 0) / 100); };
  const calcFormTotal = () => formLines.reduce((s, l) => s + calcLineTotal(l), 0);

  if (loading) return <div className="p-8 text-slate-500">Loading quotations...</div>;

  return (
    <div className="p-4 sm:p-6 md:p-8" data-testid="quotations-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black uppercase tracking-tight text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Quotations</h1>
          <p className="text-sm text-slate-500 mt-1">{total} total quotations</p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} data-testid="create-quote-btn" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm uppercase font-bold">
            <Plus size={18} className="mr-2" /> New Quotation
          </Button>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by quote number or subject..." className="pl-9 rounded-sm border-slate-300" data-testid="quote-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 rounded-sm border-slate-300" data-testid="quote-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent className="bg-white rounded-sm">
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
            <SelectItem value="SENT">Sent</SelectItem>
            <SelectItem value="ACCEPTED">Accepted</SelectItem>
            <SelectItem value="REJECTED">Rejected</SelectItem>
            <SelectItem value="INVOICED">Invoiced</SelectItem>
            <SelectItem value="CANCELLED">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="quotations-table">
            <thead className="bg-slate-100 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Quote #</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Client</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Subject</th>
                <th className="text-right px-4 py-3 text-xs font-bold text-slate-600 uppercase">Total</th>
                <th className="text-center px-4 py-3 text-xs font-bold text-slate-600 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Date</th>
                <th className="text-center px-4 py-3 text-xs font-bold text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {quotes.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">No quotations found</td></tr>
              ) : quotes.map((q) => (
                <tr key={q.id} className="hover:bg-slate-50" data-testid={`quote-row-${q.id}`}>
                  <td className="px-4 py-3 font-mono font-bold text-slate-900 text-xs">{q.quote_number}</td>
                  <td className="px-4 py-3 text-slate-700 truncate max-w-[180px]">{q.client_name || "-"}</td>
                  <td className="px-4 py-3 text-slate-600 truncate max-w-[200px]">{q.subject || "-"}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatCurrency(q.grand_total)}</td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={q.commercial_status} /></td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(q.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => viewQuote(q)} data-testid={`view-quote-${q.id}`} className="h-7 w-7 p-0 text-slate-600 hover:text-slate-900"><Eye size={15} /></Button>
                      {canEdit && <Button size="sm" variant="ghost" onClick={() => openEdit(q)} data-testid={`edit-quote-${q.id}`} className="h-7 w-7 p-0 text-slate-600 hover:text-blue-600"><Edit2 size={15} /></Button>}
                      {canDownload && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => exportQuote(q.id, "pdf")} data-testid={`export-pdf-${q.id}`} className="h-7 w-7 p-0 text-slate-600 hover:text-red-600"><FileText size={15} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => exportQuote(q.id, "excel")} data-testid={`export-excel-${q.id}`} className="h-7 w-7 p-0 text-slate-600 hover:text-green-600"><Download size={15} /></Button>
                        </>
                      )}
                      {canDelete && <Button size="sm" variant="ghost" onClick={() => deleteQuote(q.id)} data-testid={`delete-quote-${q.id}`} className="h-7 w-7 p-0 text-slate-600 hover:text-red-600"><Trash2 size={15} /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 bg-white border border-slate-200 rounded-sm p-3 gap-2">
          <p className="text-sm text-slate-500">Page {page} of {totalPages} ({total} quotations)</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="quotes-prev-page"><ChevronLeft size={16} /></Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="quotes-next-page"><ChevronRight size={16} /></Button>
          </div>
        </div>
      )}

      <QuoteViewDialog
        open={viewOpen} onOpenChange={setViewOpen} selected={selected} selectedLines={selectedLines}
        canEdit={canEdit} canDownload={canDownload}
        onApprove={approveQuote} onUpdateStatus={updateStatus} onExport={exportQuote}
      />
      <QuoteCreateDialog
        open={createOpen} onOpenChange={setCreateOpen} form={form} setForm={setForm}
        formLines={formLines} updateLine={updateLine} addLine={addLine} removeLine={removeLine}
        calcLineTotal={calcLineTotal} calcFormTotal={calcFormTotal}
        clientSearch={clientSearch} searchClients={searchClients} clients={clients}
        saving={saving} onSubmit={handleCreate}
      />
      <QuoteEditDialog
        open={editOpen} onOpenChange={setEditOpen} selected={selected} form={form} setForm={setForm}
        formLines={formLines} updateLine={updateLine} addLine={addLine} removeLine={removeLine}
        calcLineTotal={calcLineTotal} calcFormTotal={calcFormTotal}
        saving={saving} onSubmit={handleUpdateHeader}
      />
    </div>
  );
}
